import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const STORAGE_KEY = "ronavirona_consent_v1";
export const CONSENT_EVENT_OPEN = "ronavirona:open_cookie_prefs";

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

function applyConsent(c) {
  gtag("consent", "update", {
    ad_storage: c.advertising ? "granted" : "denied",
    ad_user_data: c.advertising ? "granted" : "denied",
    ad_personalization: c.advertising ? "granted" : "denied",
    analytics_storage: c.analytics ? "granted" : "denied",
    functionality_storage: c.functionality ? "granted" : "denied",
    personalization_storage: c.advertising ? "granted" : "denied",
  });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "cookie_consent_update", consent: c });
}

function persist(c) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch (_) { /* ignore */ }
}

const DEFAULT_STATE = {
  necessary: true,
  functionality: true,
  analytics: false,
  advertising: false,
};

export default function CookieBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_STATE);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
      else setPrefs({ ...DEFAULT_STATE, ...JSON.parse(stored) });
    } catch (_) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const openHandler = () => {
      setManaging(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_EVENT_OPEN, openHandler);
    return () => window.removeEventListener(CONSENT_EVENT_OPEN, openHandler);
  }, []);

  const acceptAll = () => {
    const c = { necessary: true, functionality: true, analytics: true, advertising: true };
    setPrefs(c);
    applyConsent(c);
    persist(c);
    setVisible(false);
    setManaging(false);
  };
  const rejectAll = () => {
    const c = { necessary: true, functionality: true, analytics: false, advertising: false };
    setPrefs(c);
    applyConsent(c);
    persist(c);
    setVisible(false);
    setManaging(false);
  };
  const save = () => {
    const c = { ...prefs, necessary: true };
    applyConsent(c);
    persist(c);
    setVisible(false);
    setManaging(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-[440px] z-[80]"
          data-testid="cookie-banner"
        >
          <div className="relative rounded-md bg-[#0F172A] text-[#FAF9F6] shadow-2xl border border-white/10">
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="eyebrow text-[#D97706]">cookies · consent mode v2</div>
                <button
                  type="button"
                  className="text-[#FAF9F6]/60 hover:text-[#FAF9F6]"
                  aria-label={t("cookie.close")}
                  onClick={rejectAll}
                  data-testid="cookie-close-button"
                >
                  <X size={16} />
                </button>
              </div>
              <h3 className="font-serif-display text-2xl mt-2">{t("cookie.title")}</h3>
              <p className="text-sm text-[#FAF9F6]/70 mt-2 leading-relaxed">
                {t("cookie.description")}
              </p>

              {managing && (
                <div className="mt-5 space-y-3">
                  {["necessary", "functionality", "analytics", "advertising"].map((key) => (
                    <label
                      key={key}
                      className="flex items-start gap-3 rounded-sm border border-white/10 p-3"
                    >
                      <input
                        type="checkbox"
                        checked={key === "necessary" ? true : !!prefs[key]}
                        disabled={key === "necessary"}
                        onChange={(e) => setPrefs((p) => ({ ...p, [key]: e.target.checked }))}
                        className="mt-1 h-4 w-4 accent-[#D97706]"
                        data-testid={`cookie-toggle-${key}`}
                      />
                      <span className="text-sm">
                        <span className="font-medium">{t(`cookie.categories.${key}.title`)}</span>
                        <span className="block text-[#FAF9F6]/60 mt-0.5">
                          {t(`cookie.categories.${key}.desc`)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-2">
                {!managing && (
                  <>
                    <button
                      type="button"
                      className="btn-pill btn-pill--amber text-sm"
                      onClick={acceptAll}
                      data-testid="cookie-accept-all-button"
                    >
                      {t("cookie.accept_all")}
                    </button>
                    <button
                      type="button"
                      className="btn-pill btn-pill--ghost !text-[#FAF9F6] !border-white/25 text-sm"
                      onClick={rejectAll}
                      data-testid="cookie-reject-all-button"
                    >
                      {t("cookie.reject_all")}
                    </button>
                    <button
                      type="button"
                      className="ml-auto text-xs underline text-[#FAF9F6]/75 hover:text-[#FAF9F6]"
                      onClick={() => setManaging(true)}
                      data-testid="cookie-settings-button"
                    >
                      {t("cookie.manage")}
                    </button>
                  </>
                )}
                {managing && (
                  <>
                    <button
                      type="button"
                      className="btn-pill btn-pill--amber text-sm"
                      onClick={save}
                      data-testid="cookie-save-preferences"
                    >
                      {t("cookie.save")}
                    </button>
                    <button
                      type="button"
                      className="btn-pill btn-pill--ghost !text-[#FAF9F6] !border-white/25 text-sm"
                      onClick={acceptAll}
                      data-testid="cookie-accept-all-inside"
                    >
                      {t("cookie.accept_all")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
